import { useCallback, useEffect, useState } from "react";
import { feefoApi, ApiError } from "@/api";
import type { FeefoRequest, FeefoRequestsResponse } from "@/types";
import { Button, Spinner } from "@/components/ui";
import { formatRelative } from "@/lib/format";
import { FeefoRequestRow, NewRequestDrawer } from "@/staff/feedback/feedbackShared";
import "./FeedbackTab.css";

interface Props {
  jobId: number;
  jobNumber: number;
  /** Resolved lead-or-first client for the job (what the send will target). */
  clientName: string;
  clientEmail: string;
  onError: (msg: string | undefined) => void;
  onNotice?: (kind: "info" | "warn", message: string) => void;
  /** Reports the request count up so the tab label stays in sync across sends. */
  onCount?: (n: number) => void;
}

const DAY_MS = 86_400_000;

export function FeedbackTab({ jobId, jobNumber, clientName, clientEmail, onError, onNotice, onCount }: Props) {
  const [items, setItems] = useState<FeefoRequest[] | null>(null);
  const [summary, setSummary] = useState<FeefoRequestsResponse["summary"] | null>(null);
  const [busy, setBusy] = useState<"" | "loading" | "checking" | "sending">("");
  const [modalOpen, setModalOpen] = useState(false);

  const fail = (err: unknown) => onError(err instanceof ApiError ? err.message : "Feefo request failed.");

  const load = useCallback(
    async (opts: { initial?: boolean } = {}) => {
      if (opts.initial) setBusy("loading");
      try {
        const res = await feefoApi.listFeefoRequests(jobId);
        setItems(res.items);
        setSummary(res.summary);
        onCount?.(res.summary?.count ?? res.items.length);
      } catch (err) {
        fail(err);
      } finally {
        if (opts.initial) setBusy("");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [jobId],
  );

  useEffect(() => {
    load({ initial: true });
  }, [load]);

  async function refresh() {
    if (busy) return;
    setBusy("checking");
    onError(undefined);
    try {
      const res = await feefoApi.checkFeefoRequests(jobId);
      if (res.updated > 0) {
        onNotice?.("info", `${res.updated} new review${res.updated === 1 ? "" : "s"} found.`);
      } else {
        onNotice?.("info", "No new responses yet.");
      }
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy("");
    }
  }

  async function send(description: string, stageNote: string) {
    setBusy("sending");
    onError(undefined);
    try {
      await feefoApi.createFeefoRequest({
        job_id: jobId,
        milestone: "manual",
        description,
        stage_note: stageNote,
      });
      setModalOpen(false);
      onNotice?.("info", `Review request sent to ${clientEmail}.`);
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy("");
    }
  }

  const hasEmail = clientEmail.trim() !== "";
  const rows = items ?? [];
  // Next order ref is authoritative server-side (max request_seq + 1); preview it
  // from the loaded rows so the confirm modal can show what will be generated.
  const nextSeq = (rows.length ? Math.max(...rows.map((r) => r.request_seq)) : 0) + 1;
  const predictedOrderRef = `${jobNumber}-${nextSeq}`;
  // Newest first (created_at desc); soft-warn if the last ask was < 7 days ago.
  const lastSentAt = rows.length ? rows[0].sent_at : 0;
  const recentlyAsked = lastSentAt > 0 && Date.now() - lastSentAt < 7 * DAY_MS;

  return (
    <div className="feefo">
      <div className="feefo__head">
        <div>
          <p className="feefo__blurb">
            Send Feefo review invitations at any milestone and track whether the client has responded. Each ask is an
            independent invitation — the count is the number of times this client has been asked.
          </p>
          {summary && summary.count > 0 ? (
            <div className="feefo__summary">
              <span>{summary.count} request{summary.count === 1 ? "" : "s"}</span>
              <span>{summary.responded_count} responded</span>
              {summary.responded_count > 0 ? <span>avg {summary.average_rating.toFixed(1)} ★</span> : null}
            </div>
          ) : null}
        </div>
        <div className="feefo__head-actions">
          <Button variant="ghost" size="sm" onClick={refresh} loading={busy === "checking"} disabled={!!busy}>
            Refresh
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="button"
            onClick={() => setModalOpen(true)}
            disabled={!hasEmail || !!busy}
            title={hasEmail ? undefined : "The job's client has no email address — add one on the Clients tab first."}
          >
            New feedback request
          </Button>
        </div>
      </div>

      {!hasEmail ? (
        <p className="feefo__hint">No client email resolves for this job — link a client with an email address before requesting feedback.</p>
      ) : null}

      {items === null ? (
        <div className="feefo__loading">
          <Spinner label="Loading feedback…" />
        </div>
      ) : rows.length === 0 ? (
        <p className="feefo__empty">No feedback requests yet. Send the first one when the work is at a good milestone.</p>
      ) : (
        <div className="feefo__list">
          {rows.map((r) => (
            <FeefoRequestRow key={r.id} r={r} />
          ))}
        </div>
      )}

      {modalOpen ? (
        <NewRequestDrawer
          clientName={clientName}
          clientEmail={clientEmail}
          predictedOrderRef={predictedOrderRef}
          recentlyAsked={recentlyAsked}
          lastAskedLabel={formatRelative(lastSentAt)}
          busy={busy === "sending"}
          onCancel={() => {
            if (busy === "sending") return;
            setModalOpen(false);
          }}
          onConfirm={send}
        />
      ) : null}
    </div>
  );
}
